// CPU signal — detecta capacidade de processamento single-thread.
//
// 2 fontes (ordem de confiança decrescente):
// 1. microbench por ELAPSED (`measureBenchElapsedMs` — tempo decorrido, ms)
// 2. hardwareConcurrency (cores) como floor/ceiling de sanidade
//
// **Limiares por `elapsed` (ms)** — ver docs/specs/tv-compat.md §5.3:
//   high < 8 · mid 8-20 · low 20-80 · vlow ≥ 80
// (o CpuTier não tem 'ultra' — device "acima de high" pela CPU vira 'high'
//  aqui; 'ultra' vem da combinação com RAM em `capabilities.ts`.)
//
// **NÃO é o critério final de tier do device.** Quem manda pra TV velha é o
// TETO de plataforma (`platform-tier.ts`, aplicado em `capabilities.ts`): o
// bench mede JS, mas o que trava TV antiga é compositing de GPU, que só a
// versão do motor prevê. O bench só desempata mid/high em device DESCONHECIDO
// e moderno.
//
// **Bench cacheado**: `measureBenchElapsedMs` é caro (roda ~4 passadas).
// `readCpuSignal()` pode ser chamado em fallback síncrono dentro de hot paths —
// sem cache, re-rodaria bench a cada refresh travando UI.

import { measureBenchElapsedMs } from '../benchmark'
import { clampTierToBand, platformTierBand, type PlatformTierInput } from '../platform-tier'

export type CpuTier = 'vlow' | 'low' | 'mid' | 'high'

export interface CpuSignal {
  tier: CpuTier
  cores: number
  /** Score legado do microbench (`round(1015000/elapsed)`), derivado do elapsed
   *  mediano. NÃO é mais usado pra classificar (a classificação é por elapsed) —
   *  mantido só pra telemetria/escala histórica. */
  benchScore: number
}

let cachedSignal: CpuSignal | null = null

export function readCpuSignal(platform: PlatformTierInput): CpuSignal {
  if (cachedSignal) return cachedSignal
  const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 1 : 1

  // P4 (perf/tv-hero-coldstart): em banda de plataforma TRAVADA (piso e teto
  // colapsam num ÚNICO tier), o bench é DESCARTADO downstream por
  // clampRankToBand/clampTierToBand (capabilities.ts) — rodá-lo no boot só queima
  // CPU (50-94ms medidos na LG do dono) sem mudar o resultado. Consultamos a
  // banda ANTES do bench e curto-circuitamos:
  //   • webOS gen≤5 {mid,mid} (a LG do dono) e gen≤3 {vlow,vlow}
  //   • Tizen gen≤3 {null,vlow} — teto vlow já pina vlow (nada abaixo dele)
  //   • firetv <6 / desconhecido Chr<55 {null,vlow} — idem
  // NÃO curto-circuita (o bench CONTINUA, é a única fonte real de tier):
  //   • webOS gen≥6 {mid,null}, Tizen gen4-5 {null,mid}, desconhecido/sem-banda
  // Como readCpuSignal é cacheado module-level, a 1ª chamada (initSignals no
  // boot, com a versão nativa da TV) decide — os caminhos que "redisparam" o
  // bench (ensurePlayer, useCapability, deriveAutoTier) leem o cache, então o
  // custo não reloca pra dentro da fatia crítica.
  //
  // Detecção robusta = a banda colapsa pra UM tier? Compara os extremos do range
  // de CpuTier clampados na banda: se 'vlow' e 'high' caem no MESMO tier, a banda
  // pina esse tier. Cobre {mid,mid} E {null,vlow} — um `floor === ceiling` puro
  // NÃO pegaria o teto-vlow (floor null), e a refutação exige pular Tizen gen≤3.
  const band = platformTierBand(platform)
  const bandLo = clampTierToBand('vlow', band)
  const bandHi = clampTierToBand('high', band)
  if (bandLo === bandHi && bandLo !== 'ultra') {
    // Banda travada: `bandLo` (=== bandHi) é o tier final, sem bench. O
    // `!== 'ultra'` faz o narrow Tier→CpuTier (nenhuma banda pina ultra — é
    // guarda de tipo + fallback pro bench no caso impossível).
    cachedSignal = { tier: bandLo, cores, benchScore: 0 }
    return cachedSignal
  }

  const elapsedMs = measureBenchElapsedMs()

  // Classificação por elapsed (menor = mais rápido). elapsed<=0 = sem medição
  // confiável (performance ausente) → cai no sinal fraco de cores.
  let tier: CpuTier
  if (elapsedMs <= 0) {
    tier = cores >= 4 ? 'mid' : cores >= 2 ? 'low' : 'vlow'
  } else if (elapsedMs < 8) {
    tier = 'high'
  } else if (elapsedMs < 20) {
    tier = 'mid'
  } else if (elapsedMs < 80) {
    tier = 'low'
  } else {
    tier = 'vlow'
  }

  // Floor por cores — muitos cores nunca caem em vlow/low só por um bench frio
  // (provavelmente máquina ocupada no instante da medição).
  if (cores >= 8 && tier === 'vlow') tier = 'low'
  if (cores >= 8 && tier === 'low') tier = 'mid'

  // Ceiling por cores — 1-2 cores não é "high" pro nosso uso (compositing +
  // decode competem pelo mesmo silício escasso).
  if (cores <= 2 && tier === 'high') tier = 'mid'

  // benchScore legado (telemetria) derivado do elapsed — ver comentário no tipo.
  const benchScore = elapsedMs > 0 ? Math.round(1_015_000 / elapsedMs) : 0

  cachedSignal = { tier, cores, benchScore }
  return cachedSignal
}

/** Reseta o cache. Uso interno (testing) — runtime nunca reseta CPU porque
 *  hardware não muda durante a sessão. */
export function __resetCpuSignalForTests(): void {
  cachedSignal = null
}
